/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps } from '@fluentui/react/lib/Button';
import { IIconProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { BatchTest } from 'src/luis/projects/testPane/types/BatchTest';

export type BatchTestStateCellProps = {
    /**
     * The current dataset state
     */
    batchTest: BatchTest;

    /**
     * A callback for when the run button
     * is clicked.
     */
    onRunBatchTest: () => void;

    /**
     * A callback for when the inspect
     * button is clicked.
     */
    onInspectBatchTest: () => void;
};

const messages = defineMessages({
    runState: {
        id: 'testPane.batchTestStateCell.runState',
        defaultMessage: 'Run'
    },
    runningState: {
        id: 'testPane.batchTestStateCell.runningState',
        defaultMessage: 'Running'
    },
    seeResultsState: {
        id: 'testPane.batchTestStateCell.seeResultsState',
        defaultMessage: 'See results'
    },
    failed: {
        id: 'testPane.batchTestStateCell.failed',
        defaultMessage: 'Failed'
    }
});

const { styleComponent } = getStylist('BatchTestStateCell');

const StateButton = styleComponent<IButtonProps>(ActionButton)('StateButton', theme => ({ height: theme.actionButtonHeight }));
const cellTheme = getScopedTheme('BatchTestStateCell');

/**
 * Renders the batch test current state cell in the
 * batch test table.
 */
export const BatchTestStateCell = observer((props: BatchTestStateCellProps) => {
    const { batchTest, onInspectBatchTest, onRunBatchTest } = props;
    const { localize } = useLocalization();

    switch (batchTest.state) {
        case 'running':
            return (
                <Stack horizontal tokens={{ childrenGap: 10 }} verticalAlign="center">
                    <Spinner size={SpinnerSize.small} />
                    <LuisText>{localize(messages.runningState)}</LuisText>
                </Stack>
            );
        case 'success':
            return (
                <StateButton
                    iconProps={{ iconName: 'CompletedSolid', styles: getIconStyles(cellTheme.successColor) }}
                    onClick={onInspectBatchTest}
                    onMouseDown={e => e.stopPropagation()}>
                    {localize(messages.seeResultsState)}
                </StateButton>
            );
        case 'fail':
            return (
                <LuisTooltip content={batchTest.errorMessage as string}>
                    <StateButton
                        disabled
                        iconProps={{ iconName: 'ErrorBadge', styles: getIconStyles(cellTheme.failColor) }}
                        onMouseDown={e => e.stopPropagation()}>
                        {localize(messages.failed)}
                    </StateButton>
                </LuisTooltip>
            );
        case 'notRunning':
        default:
            return (
                <StateButton iconProps={{ iconName: 'Play' }} onClick={onRunBatchTest} onMouseDown={e => e.stopPropagation()}>
                    {localize(messages.runState)}
                </StateButton>
            );
    }
});

/**
 * A helper function to get the icon styles
 * for the state buttons.
 *
 * @param color The color to give to the icon.
 */
const getIconStyles = (color: string) => classNamesFunction<IIconProps, IIconStyles>()({ root: { color: `${color} !important` } });
